import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import { fortnoxApi } from '@/services/api'

// skipErrorRedirect: the Integrations page renders its own error state for the
// connection card instead of bouncing the whole page to /error.
// `null` = never connected: React Query treats `undefined` query data as an error, so
// the service's `undefined` is mapped to `null` at this boundary.
export function useFortnoxConnection(orgID: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.fortnox.connection(orgID),
    queryFn: async () => (await fortnoxApi.getConnection(orgID)) ?? null,
    enabled: enabled && !!orgID,
    staleTime: STALE_TIME.Instant,
    meta: { skipErrorRedirect: true },
  })
}

// Starts the OAuth flow: fetches Fortnox's consent URL and navigates the whole tab to
// it. Fortnox then sends the browser to the backend's /fortnox/callback, which
// redirects back to the Integrations page with `?fortnox=<outcome>`.
export function useFortnoxConnect() {
  const { t } = useTranslation()
  return useMutation({
    mutationFn: fortnoxApi.connect,
    onSuccess: ({ authorizationUrl }) => {
      window.location.assign(authorizationUrl)
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('integrations.fortnox.connectFailed')))
    },
  })
}

export function useFortnoxDisconnect() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: fortnoxApi.disconnect,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.fortnox.all })
      toast.success(t('integrations.fortnox.disconnectedToast'))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('integrations.fortnox.disconnectFailed')))
    },
  })
}
